//@ts-check
import React, { memo, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import FeelingGoodColored from 'assets/svg/FeelingGoodColored'
import Video from 'assets/icons/Video'
import { findVideosByAgoraSubjects } from 'api/material'
import { resolveAgoraSubjects } from 'utils/agoraSubjectResolver'
import useSubjects from 'hooks/useSubjects'
import { H2 } from 'components/typography'
import Select from 'components/selects/Select'
import Button from 'components/buttons/Button'
import HangingUp from 'components/buttons/HangingUp'
import styles from './Video.module.css'

function ClassroomVideo({
  selectedVideoSubjectId,
  subjectLevel,
  subjectSublevel,
  learningVideo,
  hidden,
  onSelect,
  onHangUp
}) {
  return (
    <div className={styles.blackboard} hidden={hidden}>
      <div className={styles.innerContainer}>
        {!learningVideo && (
          <div className={styles.step1}>
            <FeelingGoodColored className={styles.svg} />
            <H2>No lo cuentes, muéstralo</H2>
            <VideoSelectorMemo
              selectedVideoSubjectId={selectedVideoSubjectId}
              subjectLevel={subjectLevel}
              subjectSublevel={subjectSublevel}
              onSelect={onSelect}
            />
          </div>
        )}
        {learningVideo && (
          <iframe
            title='Video de YouTube'
            className={styles.video}
            src={learningVideo + '?rel=0'}
            frameBorder='0'
            allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
          />
        )}
      </div>

      <div className={styles.tools}>
        <HangingUp onHangUp={onHangUp} />
        {learningVideo && (
          <div
            title='Dejar de compartir'
            className={styles.right}
            onClick={() => onSelect('', selectedVideoSubjectId)}
          >
            <Video className={styles.icons} color='var(--dark-color)' />
          </div>
        )}
      </div>
    </div>
  )
}

export default ClassroomVideo

const customStyles = {
  container: styles.customSelectContainer
}
const dedup = (ids = []) => [...new Set(ids.filter(Boolean))].sort()

function VideoSelector({
  selectedVideoSubjectId,
  subjectLevel,
  subjectSublevel,
  onSelect
}) {
  const { subjects, isFetching: isFetchingSubjects } = useSubjects()
  const [resource, setResource] = useState({
    subjectId: selectedVideoSubjectId,
    element: ''
  })

  const handleLoadVideo = () => {
    if (resource.element) onSelect(resource.element, resource.subjectId)
  }

  const agoraSubjectIds = useMemo(() => {
    if (!resource.subjectId) return []
    const { matches } = resolveAgoraSubjects(
      resource.subjectId,
      subjectLevel,
      subjectSublevel
    )
    return dedup(matches.map(m => m.agoraSubjectId))
  }, [resource.subjectId, subjectLevel, subjectSublevel])

  const hasNoMapping = !!resource.subjectId && agoraSubjectIds.length === 0

  const { data: videos = [], isFetching: isFetchingVideos } = useQuery({
    queryKey: ['videos', agoraSubjectIds],
    queryFn: () => findVideosByAgoraSubjects(agoraSubjectIds),
    enabled: agoraSubjectIds.length > 0
  })

  return (
    <div className={styles.videoSelector}>
      <Select
        label='Asignatura'
        placeholder='Selecciona asignatura'
        options={subjects.sort((a, b) => a.label.localeCompare(b.label))}
        isLoading={isFetchingSubjects}
        value={resource.subjectId}
        onChange={e => setResource({ subjectId: e.target.value, element: '' })}
        customStyles={customStyles}
      />
      <Select
        label='Vídeo'
        placeholder={
          hasNoMapping
            ? 'Sin material disponible para esta asignatura'
            : 'Selecciona vídeo'
        }
        labelKey='nombre'
        valueKey='url'
        menuPlacement='top'
        isLoading={isFetchingVideos}
        options={videos.sort((a, b) => a.nombre.localeCompare(b.nombre))}
        value={resource.element}
        onChange={e => setResource({ ...resource, element: e.target.value })}
        customStyles={customStyles}
        disabled={!resource.subjectId || hasNoMapping}
      />
      <Button
        label='Cargar vídeo'
        size='small'
        type='accent-primary'
        onClick={handleLoadVideo}
        disabled={!resource.element}
      />
    </div>
  )
}

const VideoSelectorMemo = memo(VideoSelector)
